import type { JoinMachineEntity, MachineEntity, MachineKind } from '../core/entities';
import {
  COMBINE_LATEST_COST,
  FILTER_COST,
  SWITCH_MAP_COST,
  TAKE_UNTIL_COST,
  ZIP_COST,
} from './economy';
import { MATERIALS } from './materials';
import type { MaterialId } from './materials';
import type { RecipeId } from './recipes';

export interface MachineDef {
  kind: MachineKind;
  label: string;
  /** Absent when price instead depends on the instance's own config — see {@link RecipeDef.cost} for map, {@link takeCost} for take. */
  cost?: number;
  description: string;
  expandedDescription: string;
  docsUrl: string;
  availableRecipes?: RecipeId[];
  filterableMaterials?: MaterialId[];
  /** Which materials this operator offers a `MarketService` sell method for. */
  sellableMaterials?: MaterialId[];
  availableCounts?: number[];
  /** Defaults to 1 — see {@link machineInputCount}. */
  inputs?: number;
  /**
   * Both rows of tile text, spelled out rather than derived — for an operator with no arguments to
   * put on the second row, which spends it on a hint instead. Only `zip` needs this.
   */
  tileRows?: readonly [string, string];
  /**
   * Where the name breaks when it is too long for one legible row of a 48px tile. The tail keeps the
   * parens, so the argument rides inside them and the two rows still read as one exact call —
   * `combine` / `Latest()`. Abbreviating is not an option: an RxJS name is borrowed, not coined,
   * and has to be spelled the way RxJS spells it.
   */
  nameSplit?: readonly [head: string, tail: string];
}

export const MACHINE_DEFS: Record<MachineKind, MachineDef> = {
  map: {
    kind: 'map',
    label: 'map()',
    description: 'transforms packets in a stream',
    expandedDescription:
      "Applies a function to every packet as it passes through, producing a new one from each using the machine's chosen recipe. Some recipes take a lone packet, others destructure the tuple a zip() packed. The observable stream is untouched — map always produces a new one.",
    docsUrl: 'https://rxjs.dev/api/index/function/map',
    availableRecipes: ['makeGlass', 'makeSilicon', 'makeSteel'],
  },
  filter: {
    kind: 'filter',
    label: 'filter()',
    cost: FILTER_COST,
    description: 'only lets certain packets through',
    expandedDescription:
      'Tests every packet against a predicate, passing through only the ones that satisfy it. Anything that fails the test is dropped entirely, not delayed or rerouted.',
    docsUrl: 'https://rxjs.dev/api/index/function/filter',
    filterableMaterials: Object.keys(MATERIALS) as MaterialId[],
  },
  take: {
    kind: 'take',
    label: 'take()',
    description: 'unsubscribes after N packets',
    expandedDescription:
      'Takes only the first N packets from the stream, then completes and unsubscribes automatically — the subscription cleans itself up, no manual .unsubscribe() needed.',
    docsUrl: 'https://rxjs.dev/api/index/function/take',
    availableCounts: [1, 2, 3, 4, 5, 6, 7, 8, 9],
  },
  takeUntil: {
    kind: 'takeUntil',
    label: 'takeUntil()',
    cost: TAKE_UNTIL_COST,
    description: 'unsubscribes when a Subject emits',
    expandedDescription:
      'Passes every packet through untouched until the Observable you hand it emits, and then completes — the canonical way an endless stream is shut down without anyone having to remember to unsubscribe. Its argument is a Subject, not a count, so what ends the stream is something that happens elsewhere on the board.',
    docsUrl: 'https://rxjs.dev/api/index/function/takeUntil',
    nameSplit: ['take', 'Until()'],
  },
  switchMap: {
    kind: 'switchMap',
    label: 'switchMap()',
    cost: SWITCH_MAP_COST,
    description: 'sells through the market, one sale at a time',
    expandedDescription:
      'Calls a MarketService sell method for every packet, and that method returns a stream of its own: a sale that takes time to settle before it emits what the goods fetched. switchMap subscribes to the newest sale and unsubscribes from the one before it, so a packet arriving while a sale is still in flight cancels it outright and that value is gone. Feed it slower than a sale takes to settle and nothing is ever lost.',
    docsUrl: 'https://rxjs.dev/api/index/function/switchMap',
    sellableMaterials: Object.keys(MATERIALS) as MaterialId[],
    nameSplit: ['switch', 'Map()'],
  },
  zip: {
    kind: 'zip',
    label: 'zip()',
    cost: ZIP_COST,
    description: 'packs two streams into pairs',
    expandedDescription:
      'Waits until both of its inputs have a packet, then emits the two of them as one tuple and consumes both. Its arguments are the streams wired into its sides, so there is nothing to configure — and it transforms nothing: a tuple is a pair of materials, not a product, so put a map() behind it to turn the pair into something a Subscriber can sell.',
    docsUrl: 'https://rxjs.dev/api/index/function/zip',
    inputs: 2,
    tileRows: ['zip()', 'pairs'],
  },
  combineLatest: {
    kind: 'combineLatest',
    label: 'combineLatest()',
    cost: COMBINE_LATEST_COST,
    description: 'pairs each packet with the last of the other',
    expandedDescription:
      'Emits every time either input delivers a packet, pairing it with whatever the other input delivered last — and consuming nothing, so the same remembered packet pairs again and again. That makes it the wrong tool for a recipe: one carbon arriving once turns every iron after it into steel, out of nothing. Its honest use is an input that should not be spent.',
    docsUrl: 'https://rxjs.dev/api/index/function/combineLatest',
    inputs: 2,
    nameSplit: ['combine', 'Latest'],
  },
};

/**
 * How many input ports a machine of this kind gets. Every port is capacity-1 regardless, so this is
 * only ever about how many distinct streams may feed the tile — see {@link ZipMachineEntity}.
 */
export function machineInputCount(kind: MachineKind): number {
  return MACHINE_DEFS[kind].inputs ?? 1;
}

/**
 * A join's arguments are the streams wired into its sides, so it is the one shape of operator with
 * nothing to pick off a menu: no arg picker, no "set args" prompt, and it reads as configured from
 * the moment it is placed. Kept as one predicate so the toolbar, renderer, status strip, arg picker
 * and codegen can never disagree about which kinds those are.
 */
export function isJoinKind(kind: MachineKind): boolean {
  return machineInputCount(kind) > 1;
}

export function isJoinMachine(machine: MachineEntity): machine is JoinMachineEntity {
  return isJoinKind(machine.kind);
}
